(ns ui.hyperframes-templates.player
  "The <hyperframes-player> web component (vendored below
   /hyperframes-templates/vendor/) that shows a template composition:
   looping in the templates sidebar and on the editor2 stage as the
   clip itself, where editor2 drives it like a <video> element
   (`currentTime`, `play`, `pause`).

   The variable values do not travel in the composition URL: the words
   of a caption clip are tens of KB, more than a request line holds.
   This page keeps them in `window.__hfVariablesByKey` under the key of
   the player, and the composition reads them from its parent through
   the script that `app.hyperframes-templates.handler` injects for
   `?variables-key=`. A hash of the values in the URL makes the player
   reload when they change, debounced, since a transcript edit changes
   them with every keystroke.")

(def script-src
  "/hyperframes-templates/vendor/hyperframes-player.global.js")

(def reload-delay-ms
  "How long the variables stay unchanged before the composition
   reloads with them."
  600)

(defn ensure-script!
  "Loads the player script once, the editor2 page does not include it."
  []
  (when-not (js/document.querySelector (str "script[src=\"" script-src "\"]"))
    (let [script (js/document.createElement "script")]
      (set! (.-src script) script-src)
      (.appendChild js/document.head script))))

(defn playable?
  "Whether the <hyperframes-player> `node` is upgraded to the web
   component: until the script of `ensure-script!` has loaded (or when
   it is missing, as in the build of 2026-09-22), the element is a plain
   HTMLElement without `play` / `pause` (STORRITO-V2-KG, -KF, -KH)."
  [^js node]
  (fn? (.-play node)))

(defn variables-store
  ^js []
  (or (unchecked-get js/window "__hfVariablesByKey")
      (let [store #js {}]
        (unchecked-set js/window "__hfVariablesByKey" store)
        store)))

(defn composition-src
  "The composition URL. Small values (the QA pages, a composition
   without a player key) go into the URL as `variables`, the ones of a
   keyed player are read from this page (see the namespace doc)."
  [template-id {:keys [variables variables-key]}]
  (str "/hyperframes-templates/" template-id "/index.html"
       (cond
         variables-key
         (str "?variables-key=" (js/encodeURIComponent variables-key)
              "&variables-hash=" (hash variables))

         (seq variables)
         (str "?variables="
              (js/encodeURIComponent
                (js/JSON.stringify (clj->js variables)))))))

(defn clear-reload!
  [^js node]
  (js/clearTimeout (unchecked-get node "hfReloadTimer"))
  (unchecked-set node "hfPendingSrc" nil))

(defn set-src!
  "Sets the composition URL (as attribute: the element may not be
   upgraded yet on mount) right away on mount and after the delay on an
   update, so a burst of changes reloads the composition once. The
   stage renders on every playhead move, so a render with the same
   pending URL leaves the running timer alone."
  [^js node src life-cycle]
  (cond
    (= src (.getAttribute node "src"))
    (clear-reload! node)

    (= life-cycle :replicant.life-cycle/mount)
    (.setAttribute node "src" src)

    (= src (unchecked-get node "hfPendingSrc"))
    nil

    :else
    (do
      (clear-reload! node)
      (unchecked-set node "hfPendingSrc" src)
      (unchecked-set node
                     "hfReloadTimer"
                     (js/setTimeout (fn []
                                      (unchecked-set node "hfPendingSrc" nil)
                                      (.setAttribute node "src" src))
                                    reload-delay-ms)))))

(defn pause-at!
  "Shows the composition paused at `seconds` whenever it has loaded: a
   still preview that a `play` call animates."
  [^js node seconds]
  (.addEventListener node
                     "ready"
                     (fn [_]
                       (set! (.-currentTime node) seconds)
                       (.pause node))))

(defn on-render
  [{:keys [id template-id variables on-mount paused-at]}]
  (fn [{:replicant/keys [node life-cycle] :as params}]
    (case life-cycle
      :replicant.life-cycle/unmount
      (do
        (clear-reload! node)
        (when id
          (js-delete (variables-store) id)))

      (do
        (when id
          (unchecked-set (variables-store) id (clj->js (or variables {}))))
        (when (= life-cycle :replicant.life-cycle/mount)
          (ensure-script!)
          (when paused-at
            (pause-at! node paused-at))
          (when on-mount
            (on-mount params)))
        (set-src! node
                  (composition-src template-id
                                   {:variables variables
                                    :variables-key id})
                  life-cycle)))))

(defn element
  "`width` and `height` only set the aspect ratio of the player, the
   display size comes from `style`. The player paints a black `:host`
   background by default; the compositions are transparent overlays, so
   whatever is below the player (the story on the stage, a dark card in
   the sidebar) must show through.

   The `src` is set by the hook, not by Replicant: a player with an
   `id` keeps its variables in this page, and a change reloads it
   debounced.

   `loop?` plays the composition in a loop from the start; `paused-at`
   (seconds) shows it paused there, looping once played."
  [{:keys [id width height loop? paused-at style] :as params}]
  [:hyperframes-player
   (cond-> {:width (str width)
            :height (str height)
            :muted true
            :style (merge {:background "transparent"}
                          style)
            :replicant/on-render (on-render params)}
     id (assoc :id id)
     loop? (assoc :autoplay true
                  :loop true)
     paused-at (assoc :loop true))])

(comment
  (composition-src "headline-pop" {:variables {"headline" "Hi <b>"}})
  (composition-src "captions/highlight" {:variables {"words" "[]"}
                                         :variables-key "captions-1"})
  )
